import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LargeTitle, Surface, Pill, Skeleton, EmptyState, SearchInput } from "@/components/ios";
import { FileText, ChevronRight } from "lucide-react";

const STATUS_TONE = { draft: "warning", agent_signed: "accent", issued_to_tenant: "accent", tenant_responded: "success", final: "success" };
const STATUSES = ["all", "draft", "agent_signed", "issued_to_tenant", "tenant_responded", "final"];

export default function Reports() {
  const [reps, setReps] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const nav = useNavigate();
  useEffect(() => { api.get("/reports").then(({ data }) => setReps(data)); }, []);

  const filtered = useMemo(() => {
    if (!reps) return [];
    const query = q.trim().toLowerCase();
    return reps.filter((r) => {
      if (status !== "all" && r.status !== status) return false;
      if (!query) return true;
      return [r.property?.address, r.property?.suburb, r.report_type]
        .filter(Boolean).some((s) => s.toLowerCase().includes(query));
    });
  }, [reps, q, status]);

  return (
    <div>
      <LargeTitle subtitle="Condition reports across their lifecycle">Reports</LargeTitle>

      {reps && reps.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <SearchInput testid="reports-search" value={q} onChange={setQ} placeholder="Search by address, suburb, type…" />
          <select data-testid="reports-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
            className="text-sm rounded-full px-4 py-2 outline-none border capitalize"
            style={{ background: "var(--surface)", borderColor: "var(--border)", color: "var(--text)" }}>
            {STATUSES.map((s) => <option key={s} value={s}>{s === "all" ? "All statuses" : s.replace(/_/g, " ")}</option>)}
          </select>
        </div>
      )}

      {!reps ? <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20" />)}</div>
        : reps.length === 0 ? <Surface><EmptyState icon={FileText} title="No reports yet" subtitle="Generate an AI draft from an inspection to see it here." /></Surface>
        : filtered.length === 0 ? <Surface><EmptyState icon={FileText} title="No matching reports" subtitle="Try a different search or filter." /></Surface>
        : <div className="space-y-3">
          {filtered.map((r) => (
            <Surface key={r.id} className="p-4 flex items-center gap-4 cursor-pointer active:scale-[0.995] transition-transform"
              data-testid={`report-row-${r.id}`} onClick={() => nav(`/reports/${r.id}/review`)}>
              <div className="h-11 w-11 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--accent-soft)" }}><FileText size={18} className="accent-text" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate" style={{ color: "var(--text)" }}>{r.property?.address}</div>
                <div className="text-sm text-secondary capitalize">{r.report_type} · {new Date(r.created_at).toLocaleDateString("en-AU")}</div>
              </div>
              <Pill tone={STATUS_TONE[r.status]}>{r.status.replace(/_/g, " ")}</Pill>
              <ChevronRight size={16} className="text-secondary" />
            </Surface>
          ))}
        </div>}
    </div>
  );
}
